import { useCallback, useRef } from "react";
import { TYPE } from "@chess/shared";
import { useTheme } from "@/theme/ThemeProvider";

interface Props {
  min: number;
  max: number;
  step?: number;
  value: number;
  onChange: (v: number) => void;
  format?: (n: number) => string;
}

// Brutalist single-thumb slider — the one-value sibling of BrRangeSlider.
export function BrSlider({ min, max, step = 1, value, onChange, format }: Props) {
  const p = useTheme();
  const trackRef = useRef<HTMLDivElement>(null);
  const span = Math.max(1, max - min);
  const pct = ((value - min) / span) * 100;

  const fmt = format ?? ((n: number) => String(n));

  const valueFromClientX = useCallback(
    (clientX: number) => {
      const el = trackRef.current;
      if (!el) return min;
      const rect = el.getBoundingClientRect();
      const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
      const raw = min + ratio * span;
      return Math.max(min, Math.min(max, Math.round(raw / step) * step));
    },
    [min, max, span, step],
  );

  const startDrag = (e: React.PointerEvent) => {
    e.preventDefault();
    (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
    onChange(valueFromClientX(e.clientX));
    const move = (ev: PointerEvent) => onChange(valueFromClientX(ev.clientX));
    const up = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          fontSize: 11,
          fontFamily: TYPE.mono,
          fontWeight: 800,
          marginBottom: 6,
        }}
      >
        <span>{fmt(min)}</span>
        <span>{fmt(value)}</span>
        <span>{fmt(max)}</span>
      </div>
      <div
        ref={trackRef}
        onPointerDown={startDrag}
        style={{
          position: "relative",
          height: 16,
          background: p.bg,
          border: `2px solid ${p.border}`,
          cursor: "pointer",
        }}
      >
        <div
          style={{
            position: "absolute",
            top: 0,
            bottom: 0,
            left: 0,
            width: `${pct}%`,
            background: p.accent,
            opacity: 0.5,
          }}
        />
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: `${pct}%`,
            transform: "translate(-50%, -50%)",
            width: 16,
            height: 16,
            background: p.accent,
            border: `2px solid ${p.border}`,
            cursor: "pointer",
            touchAction: "none",
          }}
        />
      </div>
    </div>
  );
}
